import { Injectable, isDevMode } from '@angular/core';
import { EncryptedData, BaseEncryptedStorage } from 'utilities';

/**
 * Virtual user ID for OIDC data - treated as a special "user" in the encrypted storage system.
 */
const OIDC_VIRTUAL_USER_ID = '__oidc_client__';

/**
 * Fixed password for OIDC encryption. In production, this could be loaded from environment.
 * Combined with PBKDF2 key derivation, this provides strong encryption for OIDC data at rest.
 */
const OIDC_ENCRYPTION_SECRET = 'keystone-oidc-aes-v1';

/**
 * Encrypted storage service specifically for OIDC authentication data.
 * Uses the same AES-GCM encryption pattern as EncryptedStorageService,
 * but operates as a dedicated virtual user to avoid conflicts with
 * the main user's encrypted storage.
 *
 * This service stores all OIDC data (tokens, user info, well-known endpoints)
 * as a single encrypted chunk in localStorage, providing strong encryption
 * for sensitive authentication data.
 */
@Injectable({
  providedIn: 'root',
})
export class OidcEncryptedStorageService extends BaseEncryptedStorage {
  private readonly SALT_KEY = '__oidc_storage_salt__';
  private readonly DATA_KEY = '__oidc_storage_data__';

  private cache: Record<string, unknown> = {};
  private isInitialized = false;
  private isSaving = false;
  private pendingSave = false;

  /** Promise that resolves when initialization is complete. Created lazily on first access. */
  private initPromise: Promise<void> | null = null;

  /**
   * Wait for initialization to complete.
   * Safe to call multiple times - returns the same promise.
   * Initialization starts on first call.
   */
  whenReady(): Promise<void> {
    if (!this.initPromise) {
      this.initPromise = this.doInitialize();
    }
    return this.initPromise;
  }

  /**
   * @deprecated Use whenReady() instead. Kept for backwards compatibility.
   */
  async initialize(): Promise<void> {
    return this.whenReady();
  }

  private async doInitialize(): Promise<void> {
    const salt = this.getOrCreateSalt(this.SALT_KEY);
    await this.initializeCrypto(OIDC_ENCRYPTION_SECRET, salt);
    await this.loadCache();
    this.isInitialized = true;

    if (isDevMode()) {
      console.log(
        '[OidcEncryptedStorageService] Initialized with virtual user:',
        OIDC_VIRTUAL_USER_ID
      );
    }
  }

  /**
   * Check if the service has been initialized.
   */
  isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Read a value from the OIDC storage.
   * Returns the raw value (not stringified) or null if not found.
   */
  read(key: string): unknown {
    if (!this.isInitialized) {
      if (isDevMode()) {
        console.warn('[OidcEncryptedStorageService] read() called before initialization');
      }
      return null;
    }
    return this.cache[key] ?? null;
  }

  /**
   * Write a value to the OIDC storage.
   *
   * **Important:** This method returns immediately after updating the in-memory cache.
   * Persistence to localStorage happens asynchronously in the background. If you need
   * to ensure the data is persisted (e.g., before page unload), call `flush()` afterward.
   *
   * Multiple rapid writes are coalesced into a single save operation for performance.
   */
  write(key: string, value: unknown): void {
    if (!this.isInitialized) {
      if (isDevMode()) {
        console.warn('[OidcEncryptedStorageService] write() called before initialization');
      }
      return;
    }

    this.cache[key] = value;
    this.scheduleSave();
  }

  /**
   * Remove a value from the OIDC storage.
   */
  remove(key: string): void {
    if (!this.isInitialized) {
      return;
    }

    if (key in this.cache) {
      delete this.cache[key];
      this.scheduleSave();
    }
  }

  /**
   * Clear all OIDC storage data.
   */
  clear(): void {
    this.cache = {};
    localStorage.removeItem(this.DATA_KEY);

    if (isDevMode()) {
      console.log('[OidcEncryptedStorageService] Storage cleared');
    }
  }

  /**
   * Get all stored keys (for debugging).
   */
  getKeys(): string[] {
    return Object.keys(this.cache);
  }

  /**
   * Get a snapshot of the cache (for debugging).
   */
  getCacheSnapshot(): Readonly<Record<string, unknown>> {
    return { ...this.cache };
  }

  /**
   * Wait for any pending save operation to complete.
   */
  async flush(): Promise<void> {
    // Wait for current save and any pending save to complete
    while (this.isSaving || this.pendingSave) {
      await new Promise(resolve => setTimeout(resolve, 10));
    }
  }

  /**
   * Schedule an async save operation. This is intentionally fire-and-forget to allow
   * write coalescing - multiple rapid writes result in a single save operation.
   *
   * Errors are logged in dev mode but not propagated, as immediate persistence is not
   * critical for OIDC data (it can be re-fetched on next login). Callers needing
   * guaranteed persistence should use `flush()` after their writes.
   */
  private scheduleSave(): void {
    if (this.isSaving) {
      // A save is in progress - mark that we need another save after it completes
      this.pendingSave = true;
      return;
    }

    this.executeAsyncSave();
  }

  /**
   * Execute an async save without awaiting.
   *
   * This is intentionally fire-and-forget: the promise is not returned or awaited.
   * Errors are caught and logged (dev mode only) rather than propagated, because:
   * 1. OIDC data can be re-fetched on next login if persistence fails
   * 2. Fire-and-forget allows write coalescing for better performance
   * 3. Callers needing synchronous confirmation should use flush()
   */
  private executeAsyncSave(): void {
    this.isSaving = true;
    this.pendingSave = false;

    this.saveCache()
      .catch(error => {
        if (isDevMode()) {
          console.error('[OidcEncryptedStorageService] Failed to save cache:', error);
        }
      })
      .finally(() => {
        this.isSaving = false;

        // If more writes happened during the save, save again to capture them
        if (this.pendingSave) {
          this.executeAsyncSave();
        }
      });
  }

  private async saveCache(): Promise<void> {
    if (!this.cryptoKey) {
      return;
    }

    const encrypted = await this.encrypt(JSON.stringify(this.cache));
    localStorage.setItem(this.DATA_KEY, JSON.stringify(encrypted));
  }

  private async loadCache(): Promise<void> {
    const storedValue = localStorage.getItem(this.DATA_KEY);

    if (!storedValue) {
      this.cache = {};
      return;
    }

    try {
      const encryptedData: EncryptedData = JSON.parse(storedValue);
      const decrypted = await this.decrypt(encryptedData);
      this.cache = JSON.parse(decrypted) as Record<string, unknown>;
    } catch (error) {
      // Decryption failed - likely due to encryption key change or corrupted data.
      // Clear corrupted data and start fresh.
      if (isDevMode()) {
        console.warn(
          '[OidcEncryptedStorageService] Failed to decrypt OIDC storage data. ' +
            'Clearing corrupted data and starting fresh.',
          error
        );
      }
      localStorage.removeItem(this.DATA_KEY);
      this.cache = {};
    }
  }
}
